import React, { useMemo, useState } from "react";
import FormActionButton from "../../components/formActionButton/formActionButton";
import { useNavigate, useSearchParams } from "react-router-dom";
import PillTitle from "../../components/pillTitle/pillTitle";
import { useI18n } from "../../utils/translate/i18n/useI18n";
import { getApiUrl } from "../../utils/getApiUrl";
import "./resetPassword.scss";

export default function ResetPassword() {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token")?.trim() ?? "";
  const initialEmail = searchParams.get("email")?.trim() ?? "";
  const isResetMode = token.length > 0;

  const [email, setEmail] = useState(initialEmail);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const title = useMemo(
    () => (isResetMode ? t("Ustaw nowe hasło") : t("Przypomnienie hasła")),
    [isResetMode, t]
  );

  const handleRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("idle");
    setMessage("");

    if (!email.trim()) {
      setStatus("error");
      setMessage(t("Wprowadź adres e-mail."));
      return;
    }

    setLoading(true);
    try {
      const resp = await fetch(getApiUrl("auth/request-password-reset"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() })
      });

      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error((data as { error?: string }).error ?? `HTTP ${resp.status}`);
      }

      setStatus("success");
      setMessage(t("Jeśli konto istnieje, wysłaliśmy link do zresetowania hasła na podany adres e-mail."));
    } catch (err: unknown) {
      setStatus("error");
      setMessage(err instanceof Error ? err.message : t("Nie udało się wysłać linku resetującego."));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("idle");
    setMessage("");

    if (!newPassword) {
      setStatus("error");
      setMessage(t("Wprowadź nowe hasło."));
      return;
    }

    if (newPassword !== confirmPassword) {
      setStatus("error");
      setMessage(t("Hasła nie są zgodne."));
      return;
    }

    setLoading(true);
    try {
      const resp = await fetch(getApiUrl("auth/reset-password"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword })
      });

      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error((data as { error?: string }).error ?? `HTTP ${resp.status}`);
      }

      setStatus("success");
      setMessage(t("Hasło zostało zaktualizowane. Możesz się teraz zalogować."));
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: unknown) {
      setStatus("error");
      setMessage(err instanceof Error ? err.message : t("Nie udało się zresetować hasła."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reset-password-page container py-4 py-md-5">
      <div className="reset-password-page__card card border-0 mx-auto">
        <div className="card-body p-4 p-md-5">
          <PillTitle variant="blue-brighter" text={title} />
          <h2 className="mt-3 mb-2">{title}</h2>
          <p className="reset-password-page__subtitle mb-4">
            {isResetMode
              ? t("Wpisz nowe hasło dla swojego konta. Link z wiadomości e-mail działa przez ograniczony czas.")
              : t("Podaj adres e-mail użyty przy logowaniu. Wyślemy link do ustawienia nowego hasła.")}
          </p>

          <form onSubmit={isResetMode ? handleResetPassword : handleRequestReset} className="reset-password-page__form">
            {!isResetMode && (
              <div className="mb-3">
                <label className="form-label">{t("Adres e-mail")}</label>
                <input
                  type="email"
                  className="form-control"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                />
              </div>
            )}

            {isResetMode && (
              <>
                <div className="mb-3">
                  <label className="form-label">{t("Nowe hasło")}</label>
                  <input
                    type="password"
                    className="form-control"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">{t("Potwierdź nowe hasło")}</label>
                  <input
                    type="password"
                    className="form-control"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </div>
              </>
            )}

            {message && (
              <div className={`alert ${status === "success" ? "alert-success" : "alert-danger"} py-2 mb-3`}>
                {message}
              </div>
            )}

            <FormActionButton
              type="submit"
              fullWidth
              iconClassName={isResetMode ? "bi bi-shield-lock-fill" : "bi bi-envelope-paper-fill"}
              disabled={loading}
              loading={loading}
              loadingLabel={isResetMode ? t("Zapisywanie…") : t("Wysyłanie linku…")}
            >
              {isResetMode ? t("Ustaw nowe hasło") : t("Wyślij link resetujący")}
            </FormActionButton>
          </form>

          <div className="reset-password-page__footer mt-3">
            <FormActionButton
              type="button"
              variant="ghost"
              size="sm"
              iconClassName="bi bi-box-arrow-in-left"
              onClick={() => navigate("/login")}
            >
              {t("Wróć do logowania")}
            </FormActionButton>
          </div>
        </div>
      </div>
    </div>
  );
}